import moment from 'moment';
import i18next from 'i18next';
import { List } from 'immutable';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { fetchCallRecordsForExtension } from 'flow/actions/extension-actions';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectOwnLatestIncomingAnsweredCallRecord } from 'flow/selectors/call-records-selectors';

import { ChatSessionKey } from 'flow/types/EntityKeys';
import { CallRecord } from 'flow/types/CallRecord';
import { TypedMap } from 'flow/types/TypedMap';

export const placeholders = [
    { key: 'name', label: i18next.t('premade_answers.variables.name') },
    { key: 'number', label: i18next.t('premade_answers.variables.phone') },
    { key: 'email', label: i18next.t('premade_answers.variables.email') },
    {
        key: 'time',
        label: i18next.t('premade_answers.variables.time'),
    },
    {
        key: 'date',
        label: i18next.t('premade_answers.variables.date'),
    },
    { key: 'receiver', label: i18next.t('premade_answers.variables.receiver') },
] as const;

type Placeholders = (typeof placeholders)[number]['key'];

const usePremadeAnswers = (key?: ChatSessionKey) => {
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const dispatch = useAppDispatch();
    const chatSessionKey = useParams<{ id: ChatSessionKey }>()?.id || key;

    useEffect(() => {
        dispatch(
            fetchCallRecordsForExtension(ownExtensionKey, [
                'contact',
                'recordings',
                'voicemessages',
                'apps',
            ]),
        );
    }, [dispatch, ownExtensionKey]);

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSessionKey]),
    );

    let receiver = '';
    if (session?.get('members', List()).size === 2) {
        const otherMember = session
            .get('members')
            .find((member) => member.get('extensionKey') !== ownExtensionKey);
        receiver = otherMember?.getIn(['contact', 'firstName'], '');
    }

    const latestIncomingCall = useAppSelector(
        selectOwnLatestIncomingAnsweredCallRecord,
    );

    const answeredTime = latestIncomingCall?.get('answered');
    const name =
        `${latestIncomingCall?.getIn(['contact', 'firstName']) ?? ''} ${
            latestIncomingCall?.getIn(['contact', 'lastName']) ?? ''
        }`.trim();

    const placeholderValues = (
        latestIncomingCall: TypedMap<CallRecord>,
        answeredTime: string,
    ) => ({
        name,
        number: latestIncomingCall?.getIn(['number', 'number']) ?? '',
        email: latestIncomingCall?.getIn(['contact', 'email']) ?? '',
        time: answeredTime ? moment(answeredTime).format('HH:mm') : '',
        date: answeredTime ? moment(answeredTime).format('YYYY-MM-DD') : '',
        receiver,
    });

    const substitutePlaceholders = (template: string) =>
        template.replace(
            /\[([^\]]+)\]/g,
            (match, key) =>
                placeholderValues(latestIncomingCall, answeredTime)[
                    key as Placeholders
                ] ?? match,
        );

    const insertPlaceholder = (
        textarea: HTMLTextAreaElement,
        variable: string,
    ) => {
        if (!textarea) return;

        const { selectionStart: start, selectionEnd: end, value } = textarea;
        const inserted = `[${variable}]`;
        const updated = value.slice(0, start) + inserted + value.slice(end);

        textarea.value = updated;
        textarea.selectionStart = textarea.selectionEnd =
            start + inserted.length;

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(
                start + inserted.length,
                start + inserted.length,
            );
        }, 0);
    };

    return { substitutePlaceholders, insertPlaceholder };
};

export default usePremadeAnswers;
